/* eslint-disable react/destructuring-assignment */
import React from 'react';

import { ReactComponent as SsvIcon } from '@/shared/assets/ssv-network.svg';
import ObolPoints from '@/shared/assets/obol-light.png';
import { ethAddress, zeroAddress } from '@/features/vaults/config';
import { THQ_ETHEREUM } from '@/features/tokens/constants/tokens';
import { cbBtcTokenAddress } from '@/features/vaults/constants/vaults/mezo/tokens/cbBtcTokenAddress';
import { mUsdTokenAddress } from '@/features/vaults/constants/vaults/mezo/tokens/mUsdTokenAddress';
import { strETHTokenAddress } from '@/features/vaults/constants/vaults/lido/tokens/strETHTokenAddress';
import { DVstETHTokenAddress } from '@/features/vaults/constants/vaults/lido/tokens/DVstETHTokenAddress';
import { GGTokenAddress } from '@/features/vaults/constants/vaults/lido/tokens/GGTokenAddress';
import { mezoUSD, mUSDC, mUSDT } from '@/features/vaults/constants/vaults/mezo/tokens/mezoUSDs';
import { mezoCbBtcTokenAddress } from '@/features/vaults/constants/vaults/mezo/tokens/mezoCbBtcTokenAddress';
import { mezoBtcTokenAddress } from '@/features/vaults/constants/vaults/mezo/tokens/mezoBtcTokenAddress';

import { SpriteIcon } from '../SpriteIcon';
import { pngIcons } from '../IconList/IconList';
import { ReactComponent as DVstETHIcon } from '../LrtIcon/DVstETHIcon.svg';
import { ReactComponent as STRETHIcon } from '../LrtIcon/strETH.svg';
import { ReactComponent as GGIcon } from '../LrtIcon/gg.svg';
type TokenIconSize = 'xs' | 'sm' | 'md';

export type TokenIconProps = {
  /**
   * Any variant of the token icon
   * Fallback to the default token icon if not provided or not found
   */
  variant?: string;
  /**
   * Format: `{network}:{address}`
   * @example `ethereum:0x1202F5C7b4B9E47a1A484E8B270be34dbbC75055`
   */
  chainAddressOrSymbol: string;
  size?: TokenIconSize;
  style?: React.CSSProperties;
};

const sizeToWidth: Record<TokenIconSize, number> = {
  xs: 20,
  sm: 24,
  md: 40,
};

const defaultStyles: Record<TokenIconSize, React.CSSProperties> = {
  xs: {
    borderRadius: '50%',
    overflow: 'hidden',
    minWidth: sizeToWidth.xs,
    minHeight: sizeToWidth.xs,
    width: sizeToWidth.xs,
    height: sizeToWidth.xs,
  },
  sm: {
    borderRadius: '50%',
    overflow: 'hidden',
    minWidth: sizeToWidth.sm,
    minHeight: sizeToWidth.sm,
    width: sizeToWidth.sm,
    height: sizeToWidth.sm,
  },
  md: {
    borderRadius: '50%',
    overflow: 'hidden',
    minWidth: sizeToWidth.md,
    minHeight: sizeToWidth.md,
    width: sizeToWidth.md,
    height: sizeToWidth.md,
  },
} as const;

type TokenComponentProps = Omit<TokenIconProps, 'chainAddressOrSymbol' | 'alt'>;

function spriteComponent(icon: string) {
  return (props: TokenComponentProps) => (
    <SpriteIcon
      icon={icon}
      size={`${sizeToWidth[props.size || 'md']}px`}
      style={{ ...(props.size && defaultStyles[props.size]), ...props.style }}
    />
  );
}

function imgComponent(src: string, alt: string) {
  return (props: TokenComponentProps) => (
    <img
      src={src}
      width={`${sizeToWidth[props.size || 'md']}px`}
      height={`${sizeToWidth[props.size || 'md']}px`}
      style={{ ...(props.size && defaultStyles[props.size]), ...props.style }}
      alt={alt}
    />
  );
}

function svgComponent(SVG: React.FunctionComponent<React.SVGProps<SVGSVGElement>>) {
  return (props: TokenComponentProps) => (
    <SVG
      width={`${sizeToWidth[props.size || 'md']}px`}
      height={`${sizeToWidth[props.size || 'md']}px`}
      style={{ ...(props.size && defaultStyles[props.size]), ...props.style }}
    />
  );
}

export const tokenIcons = {
  // ethereum
  'ethereum:0xae7ab96520DE3A18E5e111B5EaAb095312D7fE84': spriteComponent('stETH'),
  'ethereum:0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2': spriteComponent('wETH'),
  'ethereum:0x7f39C581F595B53c5cb19bD0b3f8dA6c935E2Ca0': spriteComponent('wstETH'),
  [`ethereum:${zeroAddress}`]: spriteComponent('ETH'),
  [`ethereum:${ethAddress}`]: spriteComponent('ETH'),
  'ethereum:0x9D39A5DE30e57443BfF2A8307A4256c8797A3497': spriteComponent('sUSDe'),
  'ethereum:0x57e114B691Db790C35207b2e685D4A43181e6061': spriteComponent('ENA'),
  'ethereum:0xC937e208aCd2Ea6126A3B7731C7c72f6E9307D1b': spriteComponent('wstETH'),
  'ethereum:0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599': spriteComponent('WBTC'),
  'ethereum:0x18084fbA666a33d37592fA2633fD49a74DD93a88': spriteComponent('tBTC'),
  'ethereum:0x20157DBAbb84e3BBFE68C349d0d44E48AE7B5AD2': imgComponent(pngIcons.IBTC, 'IBTC'),
  'ethereum:0x004E9C3EF86bc1ca1f0bB5C7662861Ee93350568': imgComponent(pngIcons.uniBTC, 'uniBTC'),
  'ethereum:0x57B946008913B82E4dF85f501cbAeD910e58D26C': imgComponent(pngIcons.POND, 'POND'),
  'ethereum:0x1202F5C7b4B9E47a1A484E8B270be34dbbC75055': imgComponent(pngIcons.wstUSR, 'wstUSR'),
  'ethereum:0x6bedE1c6009a78c222D9BDb7974bb67847fdB68c': imgComponent(pngIcons.USBD, 'USBD'),
  'ethereum:0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48': spriteComponent('USDC'),
  'ethereum:0x9D65fF81a3c488d585bBfb0Bfe3c7707c7917f54': svgComponent(SsvIcon),
  'ethereum:0x0B010000b7624eb9B3DfBC279673C76E9D29D5F7': imgComponent(ObolPoints, 'OBOL'),
  'ethereum:0xd38BB40815d2B0c2d2c866e0c72c5728ffC76dd9': imgComponent(pngIcons.SIS, 'SIS'),
  'ethereum:0xfE0ccc9942E98C963Fe6b4e5194EB6e3Baa4cb64': imgComponent(pngIcons.sYUSD, 'sYUSD'),
  'ethereum:0xf0DB65D17e30a966C2ae6A21f6BBA71cea6e9754': imgComponent(pngIcons.BARD, 'BARD'),
  'ethereum:0xdAC17F958D2ee523a2206206994597C13D831ec7': imgComponent(pngIcons.USDT, 'USDT'),
  'ethereum:0xdC035D45d973E3EC169d2276DDab16f1e407384F': imgComponent(pngIcons.USDS, 'USDS'),
  [`ethereum:${THQ_ETHEREUM}`]: imgComponent(pngIcons.THQ, 'THQ'),
  [`ethereum:${cbBtcTokenAddress}`]: imgComponent(pngIcons.cbBTC, 'cbBTC'),
  [`mezo:${mezoCbBtcTokenAddress}`]: imgComponent(pngIcons.cbBTC, 'cbBTC'),
  [`mezo:${mezoBtcTokenAddress}`]: imgComponent(pngIcons.BTC, 'BTC'),
  [`mezo:${zeroAddress}`]: imgComponent(pngIcons.BTC, 'BTC'),
  [`ethereum:${mUsdTokenAddress}`]: imgComponent(pngIcons.mUSD, 'mUSD'),
  [`mezo:${mezoUSD}`]: imgComponent(pngIcons.mUSD, 'mUSD'),
  [`mezo:${mUSDC}`]: imgComponent(pngIcons.USDC, 'mUSDC'),
  [`mezo:${mUSDT}`]: imgComponent(pngIcons.USDT, 'mUSDT'),
  [`ethereum:${strETHTokenAddress}`]: svgComponent(STRETHIcon),
  [`ethereum:${DVstETHTokenAddress}`]: svgComponent(DVstETHIcon),
  [`ethereum:${GGTokenAddress}`]: svgComponent(GGIcon),
  // Lisk
  'lisk:0x76D8de471F54aAA87784119c60Df1bbFc852C415': spriteComponent('wstETH'),
  'lisk:0x9BFA177621119e64CecbEabE184ab9993E2ef727': imgComponent(pngIcons.mBTC, 'M-BTC'),
  'lisk:0xac485391EB2d7D88253a7F1eF18C37f4242D1A24': imgComponent(pngIcons.LSK, 'LSK'),
  'reward:lisk:0xac485391EB2d7D88253a7F1eF18C37f4242D1A24': imgComponent(
    pngIcons.LiskReward,
    'LiskReward',
  ),
  // Fraxtal
  'fraxtal:0xFc00000000000000000000000000000000000002': imgComponent(pngIcons.FRAX, 'WFRAX'),
  // Base
  [`base:${zeroAddress}`]: spriteComponent('ETH'),
  [`base:${ethAddress}`]: spriteComponent('ETH'),
  'base:0x4200000000000000000000000000000000000006': spriteComponent('wETH'),
  'base:0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913': imgComponent(pngIcons.USDC, 'USDC'),
  'base:0xfde4C96c8593536E31F229EA8f37b2ADa2699bb2': imgComponent(pngIcons.USDT, 'USDT'),
  'base:0x98d0baa52b2D063E780DE12F615f963Fe8537553': imgComponent(pngIcons.KAITO, 'KAITO'),
  'base:0x0b2558bdBC7FFEC0f327fB3579c23daBD1699706': imgComponent(pngIcons.THQ, 'THQ'),
  // BSC
  'bsc:0x5845684b49aEf79A5c0F887f50401C247dca7AC6': imgComponent(pngIcons.CYC, 'CYC'),
  'bsc:0xabE8E5CabE24Cb36df9540088fD7cE1175b9bc52': imgComponent(pngIcons.SOLV, 'SOLV'),
  // holesky
  'holesky:0x94373a4919B3240D86eA41593D5eBa789FEF3848': spriteComponent('wETH'),
  'holesky:0x8d09a4502Cc8Cf1547aD300E066060D043f6982D': spriteComponent('wstETH'),
  'holesky:0x3F1c547b21f65e10480dE3ad8E19fAAC46C95034': spriteComponent('STETH'),
  [`holesky:${zeroAddress}`]: spriteComponent('ETH'),
  [`holesky:${ethAddress}`]: spriteComponent('ETH'),
  // hoodi
  'hoodi:0xdb7057418E9272216551a6eA07876a1e26D306f9': spriteComponent('wETH'),
  'hoodi:0x7E99eE3C66636DE415D2d7C880938F2f40f94De4': spriteComponent('wstETH'),
  [`hoodi:${zeroAddress}`]: spriteComponent('ETH'),
  [`hoodi:${ethAddress}`]: spriteComponent('ETH'),
  // Monad
  [`monad:${ethAddress}`]: imgComponent(pngIcons.MON, 'MON'),
  [`monad:${zeroAddress}`]: imgComponent(pngIcons.MON, 'MON'),
  'monad:0x3bd359C1119dA7Da1D913D1C4D2B7c461115433A': imgComponent(pngIcons.MON, 'WMON'),
  // Monad testnet
  [`monad-testnet:${ethAddress}`]: imgComponent(pngIcons.MON, 'MON'),
  [`monad-testnet:${zeroAddress}`]: imgComponent(pngIcons.MON, 'MON'),
  'monad-testnet:0x760AfE86e5de5fa0Ee542fc7B7B713e1c5425701': imgComponent(pngIcons.MON, 'WMON'),
  // Rootstock
  [`rootstock:${zeroAddress}`]: imgComponent(pngIcons.RBTC, 'RBTC'),
  [`rootstock:${ethAddress}`]: imgComponent(pngIcons.RBTC, 'RBTC'),
  'rootstock:0x967F8799aF07dF1534d48A95a5C9FEBE92c53AE0': imgComponent(pngIcons.RBTC, 'WRBTC'),
};

export const TokenIcon = ({
  variant,
  chainAddressOrSymbol,
  size = 'md',
  style,
}: TokenIconProps) => {
  const Component =
    tokenIcons[variant != null ? `${variant}:${chainAddressOrSymbol}` : chainAddressOrSymbol] ||
    tokenIcons[chainAddressOrSymbol];

  if (!Component) {
    return null;
  }

  return Component({ size, style });
};
